// KhatabookPayment.jsx
import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch, FaEdit, FaTrash, FaPlus, FaFilter, FaArrowUp, FaArrowDown } from "react-icons/fa";
import Swal from 'sweetalert2';
import { useLocation } from 'react-router-dom';

function KhatabookPayment() {
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    // Tab state
    const searchParams = new URLSearchParams(location.search);
    const typeParam = searchParams.get('type');
    const [activeTab, setActiveTab] = useState(typeParam || 'ALL'); // 'ALL', 'IN', 'OUT'

    // Modal states
    const [transactionModalOpen, setTransactionModalOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // Filter states
    const [filters, setFilters] = useState({
        searchValue: '',
        dateFrom: '',
        dateTo: '',
        bank_id: '',
        party_id: '',
        type: ''
    });

    // Form state for add/edit
    const [formData, setFormData] = useState({
        date: '',
        time: '',
        amount: '',
        type: 'IN',
        bank_id: '',
        party_id: '',
        bank_txn: '',
        remark: ''
    });

    // Dropdown options
    const [banks, setBanks] = useState([]);
    const [parties, setParties] = useState([]);

    // Summary stats
    const [summary, setSummary] = useState({
        totalTransactions: 0,
        totalIn: 0,
        totalOut: 0,
        todayIn: 0,
        todayOut: 0
    });

    // Fetch Banks for dropdown
    const fetchBanks = async () => {
        try {
            const result = await apiCall("banks", { limit: 9999 }, true);
            if (result.status === true) {
                setBanks(result.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch banks:", error);
        }
    };

    // Fetch Parties for dropdown
    const fetchParties = async () => {
        try {
            const result = await apiCall("parties", { limit: 9999 }, true);
            if (result.status === true) {
                setParties(result.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch parties:", error);
        }
    };

    // Calculate summary
    const calculateSummary = (data) => {
        const today = new Date().toISOString().split('T')[0];
        let totalIn = 0, totalOut = 0, todayIn = 0, todayOut = 0;

        data.forEach(t => {
            const amount = parseFloat(t.amount);
            if (t.type === 'IN') {
                totalIn += amount;
                if (t.date === today) todayIn += amount;
            } else if (t.type === 'OUT') {
                totalOut += amount;
                if (t.date === today) todayOut += amount;
            }
        });

        setSummary({
            totalTransactions: data.length,
            totalIn: totalIn,
            totalOut: totalOut,
            todayIn: todayIn,
            todayOut: todayOut
        });
    };

    // Fetch Transactions with pagination
    const fetchTransactions = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                type: activeTab === 'ALL' ? '' : activeTab,
                searchValue: filters.searchValue,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                bank_id: filters.bank_id,
                party_id: filters.party_id,
                page: page,
                limit: pagination.perPage
            };

            const result = await apiCall(
                "khatabook-transactions",
                params,
                true
            );

            if (result.status === true) {
                setTransactions(result.data || []);
                setFilteredTransactions(result.data || []);
                calculateSummary(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || "Failed to fetch transactions");
                setTransactions([]);
                setFilteredTransactions([]);
                setSummary({
                    totalTransactions: 0,
                    totalIn: 0,
                    totalOut: 0,
                    todayIn: 0,
                    todayOut: 0
                });
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setTransactions([]);
            setFilteredTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Tab Change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Handle Add Transaction
    const handleAddTransaction = () => {
        setIsEditMode(false);
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const time = now.toTimeString().slice(0, 5);

        setFormData({
            date: today,
            time: time,
            amount: '',
            type: 'IN',
            bank_id: '',
            party_id: '',
            bank_txn: '',
            remark: ''
        });
        setSelectedTransaction(null);
        setTransactionModalOpen(true);
    };

    // Handle Edit Transaction
    const handleEditTransaction = (transaction) => {
        setIsEditMode(true);
        setSelectedTransaction(transaction);
        setFormData({
            date: transaction.date || '',
            time: transaction.time || '',
            amount: transaction.amount || '',
            type: transaction.type || 'IN',
            bank_id: transaction.bank_id || '',
            party_id: transaction.party_id || '',
            bank_txn: transaction.bank_txn || '',
            remark: transaction.remark || ''
        });
        setTransactionModalOpen(true);
    };

    // Handle Delete Transaction
    const handleDeleteTransaction = (transaction) => {
        Swal.fire({
            title: 'Delete Payment?',
            text: `Are you sure you want to delete this payment of ₹${transaction.amount}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Delete!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "delete-transaction",
                        {
                            txn_id: transaction.txn_id
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success("Payment deleted successfully!");
                        fetchTransactions(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to delete payment");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Save Transaction
    const handleSaveTransaction = async () => {
        if (!formData.date) {
            toast.error("Please select date");
            return;
        }

        if (!formData.time) {
            toast.error("Please select time");
            return;
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }

        if (!formData.bank_id) {
            toast.error("Please select a bank account");
            return;
        }

        if (!formData.party_id) {
            toast.error("Please select a party");
            return;
        }

        try {
            const endpoint = isEditMode ? "update-transaction" : "add-transaction";
            const params = isEditMode
                ? {
                    txn_id: selectedTransaction.txn_id,
                    date: formData.date,
                    time: formData.time,
                    amount: formData.amount,
                    type: formData.type,
                    bank_id: formData.bank_id,
                    party_id: formData.party_id,
                    bank_txn: formData.bank_txn,
                    remark: formData.remark
                }
                : {
                    date: formData.date,
                    time: formData.time,
                    amount: formData.amount,
                    type: formData.type,
                    bank_id: formData.bank_id,
                    party_id: formData.party_id,
                    bank_txn: formData.bank_txn,
                    remark: formData.remark
                };

            const response = await apiCall(endpoint, params, true);

            if (response.status === true) {
                toast.success(isEditMode ? "Payment updated successfully!" : "Payment added successfully!");
                setTransactionModalOpen(false);
                fetchTransactions(pagination.currentPage);
            } else {
                toast.error(response.message || "Failed to save payment");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchBanks();
        fetchParties();
    }, []);

    useEffect(() => {
        fetchTransactions(1);
    }, [activeTab]);

    // Apply filters
    const applyFilters = () => {
        fetchTransactions(1);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            searchValue: '',
            dateFrom: '',
            dateTo: '',
            bank_id: '',
            party_id: '',
            type: ''
        });
        fetchTransactions(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchTransactions(newPage);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Get type badge color
    const getTypeColor = (type) => {
        return type === 'IN' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
    };

    // Get type icon
    const getTypeIcon = (type) => {
        return type === 'IN' ? <FaArrowUp className="text-green-600" /> : <FaArrowDown className="text-red-600" />;
    };

    // Get bank name by ID
    const getBankName = (bankId) => {
        const bank = banks.find(b => b.bank_id == bankId);
        return bank ? bank.name : 'N/A';
    };

    // Get party name by ID
    const getPartyName = (partyId) => {
        const party = parties.find(p => p.party_id == partyId);
        return party ? party.name : 'N/A';
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Khatabook Payments</h1>
                            <p className="text-gray-600 text-sm">View and manage all payments</p>
                        </div>
                        <button
                            onClick={handleAddTransaction}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm flex items-center gap-2"
                        >
                            <FaPlus />
                            Add Payment
                        </button>
                    </div>

                    {/* Tab Section - ALL, IN, OUT */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className="border-b border-gray-200">
                            <div className="flex">
                                <button
                                    onClick={() => handleTabChange('ALL')}
                                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 ${activeTab === 'ALL'
                                        ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    All Payments
                                </button>
                                <button
                                    onClick={() => handleTabChange('IN')}
                                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 ${activeTab === 'IN'
                                        ? 'border-b-2 border-green-500 text-green-600 bg-green-50'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <FaArrowUp className="text-green-600" />
                                    Payment IN
                                </button>
                                <button
                                    onClick={() => handleTabChange('OUT')}
                                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 ${activeTab === 'OUT'
                                        ? 'border-b-2 border-red-500 text-red-600 bg-red-50'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <FaArrowDown className="text-red-600" />
                                    Payment OUT
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className={`p-4 sm:p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {/* Search */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                    <input
                                        type="text"
                                        value={filters.searchValue}
                                        onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                        placeholder="Search by bank txn or remark..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    />
                                </div>

                                {/* Date From */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                                    <input
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    />
                                </div>

                                {/* Date To */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                                    <input
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    />
                                </div>

                                {/* Type Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                                    <select
                                        value={filters.type}
                                        onChange={(e) => handleFilterChange('type', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        <option value="">All Types</option>
                                        <option value="IN">Payment IN</option>
                                        <option value="OUT">Payment OUT</option>
                                    </select>
                                </div>

                                {/* Bank Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
                                    <select
                                        value={filters.bank_id}
                                        onChange={(e) => handleFilterChange('bank_id', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        <option value="">All Banks</option>
                                        {banks.map(bank => (
                                            <option key={bank.bank_id} value={bank.bank_id}>{bank.name} ({bank.bank})</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Party Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Party</label>
                                    <select
                                        value={filters.party_id}
                                        onChange={(e) => handleFilterChange('party_id', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        <option value="">All Parties</option>
                                        {parties.map(party => (
                                            <option key={party.party_id} value={party.party_id}>{party.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2 items-end col-span-1 sm:col-span-2 lg:col-span-2">
                                    <button onClick={applyFilters} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm flex items-center justify-center gap-2">
                                        <FaFilter /> Apply Filters
                                    </button>
                                    <button onClick={resetFilters} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">Reset</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <p className="text-sm text-gray-600">Total Payments</p>
                            <p className="text-2xl font-bold text-gray-900">{summary.totalTransactions}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <p className="text-sm text-gray-600">Total Payment IN</p>
                            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.totalIn)}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <p className="text-sm text-gray-600">Total Payment OUT</p>
                            <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.totalOut)}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <p className="text-sm text-gray-600">Today's Payment IN</p>
                            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.todayIn)}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <p className="text-sm text-gray-600">Today's Payment OUT</p>
                            <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.todayOut)}</p>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredTransactions.length}</span> payments
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {loading && (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* Transactions Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Txn</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remark</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredTransactions.length > 0 ? (
                                        filteredTransactions.map((transaction) => (
                                            <tr key={transaction.txn_id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">#{transaction.txn_id}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm text-gray-900">{transaction.date} {transaction.time}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`text-sm font-semibold ${transaction.type === 'IN' ? 'text-green-600' : 'text-red-600'}`}>
                                                        {formatCurrency(transaction.amount)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${getTypeColor(transaction.type)}`}>
                                                        {getTypeIcon(transaction.type)}
                                                        {transaction.type === 'IN' ? 'Payment IN' : 'Payment OUT'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                                        {getPartyName(transaction.party_id)}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                                        {getBankName(transaction.bank_id)}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className="text-sm text-gray-600 font-mono">
                                                        {transaction.bank_txn || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 max-w-xs">
                                                    <span className="text-sm text-gray-600 truncate block" title={transaction.remark}>
                                                        {transaction.remark || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={() => handleEditTransaction(transaction)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                            <FaEdit className="text-sm" />
                                                        </button>
                                                        <button onClick={() => handleDeleteTransaction(transaction)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                                            <FaTrash className="text-sm" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <FaArrowUp className="w-12 h-12 text-gray-300 mb-3" />
                                                    <p className="text-sm">No payments found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or add a new payment</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">Page {pagination.currentPage} of {pagination.totalPages}</div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                >
                                    Previous
                                </button>
                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) pageNum = i + 1;
                                        else if (pagination.currentPage <= 3) pageNum = i + 1;
                                        else if (pagination.currentPage >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                                        else pageNum = pagination.currentPage - 2 + i;
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Payment Modal */}
            {transactionModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setTransactionModalOpen(false)}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        {isEditMode ? 'Edit Payment' : 'Add New Payment'}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {isEditMode ? `Editing payment #${selectedTransaction?.txn_id}` : 'Create a new payment'}
                                    </p>
                                </div>
                                <button onClick={() => setTransactionModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-4">
                                    {/* Payment Type */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type <span className="text-red-500">*</span></label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, type: 'IN' })}
                                                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${formData.type === 'IN'
                                                    ? 'bg-green-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                Payment IN
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, type: 'OUT' })}
                                                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${formData.type === 'OUT'
                                                    ? 'bg-red-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                Payment OUT
                                            </button>
                                        </div>
                                    </div>

                                    {/* Date */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
                                        <input
                                            type="date"
                                            value={formData.date}
                                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    {/* Time */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Time <span className="text-red-500">*</span></label>
                                        <input
                                            type="time"
                                            value={formData.time}
                                            onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    {/* Amount */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) <span className="text-red-500">*</span></label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            placeholder="Enter amount..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    {/* Party */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Party <span className="text-red-500">*</span></label>
                                        <select
                                            value={formData.party_id}
                                            onChange={(e) => setFormData({ ...formData, party_id: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="">Select Party</option>
                                            {parties.map(party => (
                                                <option key={party.party_id} value={party.party_id}>
                                                    {party.name} ({party.mobile})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Bank */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account <span className="text-red-500">*</span></label>
                                        <select
                                            value={formData.bank_id}
                                            onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="">Select Bank</option>
                                            {banks.map(bank => (
                                                <option key={bank.bank_id} value={bank.bank_id}>
                                                    {bank.name} - {bank.bank} ({bank.account})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Bank Transaction Reference */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Bank Transaction Reference</label>
                                        <input
                                            type="text"
                                            value={formData.bank_txn}
                                            onChange={(e) => setFormData({ ...formData, bank_txn: e.target.value })}
                                            placeholder="Enter bank transaction reference..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    {/* Remark */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Remark</label>
                                        <textarea
                                            value={formData.remark}
                                            onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                                            placeholder="Enter remark..."
                                            rows="3"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end gap-2">
                                <button onClick={() => setTransactionModalOpen(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">
                                    Cancel
                                </button>
                                <button onClick={handleSaveTransaction} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm">
                                    {isEditMode ? 'Update' : 'Add'} Payment
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster position="top-right" />
        </div>
    );
}

export default KhatabookPayment;